import Cookies from 'js-cookie'

import BuildConfig from '$extensionSrc/buildConfig'
import { RedditSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { sendAlertNotification } from '$extensionSrc/utils/communication'
import {
  PLATFORM,
  StageType,
  RedditRecommendationKey,
} from '$extensionSrc/utils/enums'
import { _debug } from '$extensionSrc/utils/logging'

import { NO_TIMEOUT } from '../../consts/stageMachineConsts'
import { StageDef } from '../automationMachineInterfaces'
import {
  getStoredMachineOverlayStatus,
  setStoredMachineOverlayStatus,
} from '../automationStageMachine'
import { wait } from '../interruptiblePromises'
import {
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import {
  deleteContent,
  fetchMeJson,
  getContentIdIterator,
  getJitteredValue,
  pluralize,
  redactContent,
  RedditContentType,
  redditFetch,
} from './contentUtils'
import * as redditUrls from './redditUrls'
import { getInitialValueBySettingKey } from '../utils'

type RedditPostDeleteState = {
  count: number // total count of posts deleted all-time
  message: string // Used for the RecommendationCard footer text
  result?: {
    // Used for the RecommendationCard banner
    title: string
    detail?: string
  }
  username?: string
}

function buildResultObject(
  totalDeleted: number,
  nDeleted: number,
  username: string,
): RedditPostDeleteState {
  let result: { title: string; detail?: string } | undefined
  if (nDeleted === 0) {
    result = {
      title: 'No posts deleted',
      detail: 'We were not able to find any posts to delete on your profile',
    }
  } else {
    result = {
      title: `You deleted ${pluralize(nDeleted, RedditContentType.Post)}.`,
    }
  }

  return {
    count: totalDeleted,
    message: `Deleted ${pluralize(totalDeleted, RedditContentType.Post)} (all-time).`,
    result,
    username,
  }
}

async function updateMachineOverlayStatus(nDeleted: number) {
  const currentStatus = await getStoredMachineOverlayStatus(PLATFORM.REDDIT)
  return setStoredMachineOverlayStatus(PLATFORM.REDDIT, {
    ...currentStatus,
    nDeleted,
  })
}

async function redactPost(postId: string) {
  const csrfToken = Cookies.get('csrf_token')

  if (!csrfToken) {
    _debug(
      'CSRF token missing for GQL post edit, falling back to legacy API',
      'reddit.redactPost',
      'w',
    )
    return redactContent(postId)
  }

  const formData = new URLSearchParams({
    content:
      '{"document":[{"e":"par","c":[{"e":"text","t":"[deleted]","f":[[0,0,9]]}]}]}',
    mode: 'richText',
    csrf_token: csrfToken,
  })

  try {
    return await redditFetch(
      `https://www.reddit.com/svc/shreddit/edit-post/${postId}`,
      {
        body: formData.toString(),
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
        },
      },
    )
  } catch (error) {
    _debug(
      `GQL post edit failed for ${postId}: ${error.message}`,
      'reddit.redactPost',
      'e',
    )
    return redactContent(postId)
  }
}

async function deletePost(postId: string) {
  const csrfToken = Cookies.get('csrf_token')

  if (!csrfToken) {
    _debug(
      'CSRF token missing for GQL post delete, falling back to legacy API',
      'reddit.deletePost',
      'w',
    )
    return deleteContent(postId)
  }

  const gqlBody = {
    operation: 'DeletePost',
    variables: {
      input: {
        postId,
      },
    },
    csrf_token: csrfToken,
  }

  try {
    return await redditFetch(redditUrls.apiGqlUrl, {
      body: JSON.stringify(gqlBody),
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
      },
    })
  } catch (error) {
    _debug(
      `GQL post delete failed for ${postId}: ${error.message}`,
      'reddit.deletePost',
      'e',
    )
    return deleteContent(postId)
  }
}

const getCurrentValueFn = () => () => ''

const getIsFulfilledFn = () => () => true

const StageKey = {
  UPDATE_LOAD_OVERVIEW_PAGE: 'UPDATE_LOAD_OVERVIEW_PAGE',
  UPDATE_REDACT_DELETE_POSTS: 'UPDATE_REDACT_DELETE_POSTS',
  UPDATE_REFRESH_FINAL: 'UPDATE_REFRESH_FINAL',
}

const gatherStageDefs: StageDef[] = []

const getCantUndoFn = () => () => true

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const updateStageDefs: StageDef[] = [
  // Navigate to the user's overview page, load the user's username and clear run stats
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_LOAD_OVERVIEW_PAGE,
    status: {
      title: 'Getting Started...',
      body: 'Gathering account information.',
    },
    url: redditUrls.profileUrl,
    urlPattern: 'https://www.reddit.com/user/.+',
    action: async (_machineContext, userContext) => {
      const { initialValues: storedInitialValues, updateStoredInitialValues } =
        userContext

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        RedditSettingKey.DELETE_POSTS,
      )

      try {
        // Throws error if username is not found
        const { username } = await fetchMeJson()

        const updatedInitialValues = {
          [RedditSettingKey.DELETE_POSTS]: {
            ...initialValues,
            stats: {
              latestRunDeleted: 0,
            },
            username,
          },
        }

        await updateStoredInitialValues(
          updatedInitialValues,
          RedditRecommendationKey.REDDIT_DELETE_POSTS,
        )
      } catch (error) {
        _debug(
          `Failed to gather user info: ${error.message}`,
          'reddit.loadOverviewStage',
          'e',
        )
        sendAlertNotification(
          PLATFORM.REDDIT,
          'Reddit: Error',
          'Unable to get account information',
        )
        throw error
      }
    },
  },
  // Redact and delete posts
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_REDACT_DELETE_POSTS,
    status: {
      title: `${BuildConfig.TENANT_NAME} is deleting your posts...`,
      body: 'Please keep this tab open.',
    },
    timeout: NO_TIMEOUT,
    action: async (_machineContext, userContext, additionalMachineData) => {
      const {
        incrementTotalCount,
        initialValues: storedInitialValues,
        isDry,
        updateStoredInitialValues,
      } = userContext
      const { resumeData } = additionalMachineData
      const { username } = await fetchMeJson()

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        RedditSettingKey.DELETE_POSTS,
      )

      try {
        const postIdIter = getContentIdIterator(
          username,
          RedditContentType.Post,
        )

        let totalDeleted = initialValues?.count || 0
        let currentDeleted = resumeData?.data?.deletedPosts || 0

        for await (const postId of postIdIter) {
          if (isDry) {
            await wait(getJitteredValue(500, 250))
          } else {
            await redactPost(postId)
            await wait(getJitteredValue(500, 250))
            await deletePost(postId)
            await wait(getJitteredValue(500, 250))
          }

          totalDeleted += 1
          currentDeleted += 1

          await incrementTotalCount(RedditRecommendationKey.REDDIT_DELETE_POSTS)

          await updateMachineOverlayStatus(currentDeleted)
          await resumeData.set({
            ...resumeData.data,
            deletedPosts: currentDeleted,
          })

          await updateStoredInitialValues(
            {
              [RedditSettingKey.DELETE_POSTS]: buildResultObject(
                totalDeleted,
                currentDeleted,
                username,
              ),
            },
            RedditRecommendationKey.REDDIT_DELETE_POSTS,
          )

          await userContext.updateStatisticsLastCount(
            RedditRecommendationKey.REDDIT_DELETE_POSTS,
            currentDeleted,
          )
        }
      } catch (error) {
        _debug(
          `Failed to complete post deletion: ${error.message}`,
          'reddit.deletePostsStage',
          'e',
        )
        sendAlertNotification(
          PLATFORM.REDDIT,
          'Reddit: Delete Posts Error',
          'Failed to complete post deletion',
        )
        throw error
      }
    },
  },
  // Refresh the page to show the content has been deleted
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_REFRESH_FINAL,
    status: {
      title: 'Wrapping up...',
      body: 'Reloading the page.',
    },
    url: redditUrls.profileUrl,
    urlPattern: 'https://www.reddit.com/user/.+',
    action: async (_machineContext, _userContext, additionalMachineData) => {
      const { resumeData } = await additionalMachineData
      await resumeData.clear()
      window.location.reload()
    },
  },
]

const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
